import {startCase} from "lodash";
import {
  CARD_PAYMENTS_RETRY_FLOW_TYPE,
  CardPaymentsRetryFlow,
  CardPaymentsRetryPolicy,
  PaymentFlowFeatureFlag,
  PaymentFlowUpdatePayload,
} from "../../../../apis/requests/payment-flows/types";
import {ParamSchema} from "../../../../apis/requests/segments/types";
import {
  buildConditionsPayload,
  flowToBaseForm,
  getDefaultControlPolicy,
  getProductionPolicy,
} from "../../flow-form/helpers";
import {EMPTY_POLICY, RETRY_RULES, WEEKDAYS} from "./constants";
import type {FormValues, PolicyFormValues, VariantFormValues} from "./schema";

const DAY_RANGE_PATTERN = /^\s*(\d+)\s*(?:[-–]\s*(\d+))?\s*$/;

export type DayRange = {start: number; end: number};

export const parseDayRange = (value: string): DayRange | null => {
  const match = DAY_RANGE_PATTERN.exec(value);
  if (!match) return null;
  const start = Number(match[1]);
  const end = Number(match[2] ?? match[1]);
  return start <= end ? {start, end} : null;
};

const formatDayRange = ({start, end}: DayRange): string => (start === end ? `${start}` : `${start}-${end}`);

export const formatDays = (days: number[]): string => {
  const ranges: DayRange[] = [];
  days.forEach((day) => {
    const last = ranges[ranges.length - 1];
    if (last && last.end === day - 1) {
      last.end = day;
    } else {
      ranges.push({start: day, end: day});
    }
  });
  return ranges.map(({start, end}) => (start === end ? `${start}` : `${start}–${end}`)).join(", ");
};

export const formatHours = (hours: number[]): string =>
  `${hours.length > 1 ? "Hours" : "Hour"} ${[...hours].sort((a, b) => a - b).join(", ")}`;

export type ScheduleCoverage = {uncovered: number[]; overlapping: number[]};

export const getScheduleCoverage = (schedule: PolicyFormValues["schedule"], maxDay: number): ScheduleCoverage => {
  const coverCount = new Map<number, number>();
  schedule.forEach((row) => {
    const range = parseDayRange(row.days);
    if (!range) return;
    for (let day = range.start; day <= range.end; day++) {
      coverCount.set(day, (coverCount.get(day) ?? 0) + 1);
    }
  });
  const days = Array.from({length: Math.max(maxDay, 0)}, (_, index) => index + 1);
  return {
    uncovered: days.filter((day) => !coverCount.has(day)),
    overlapping: days.filter((day) => (coverCount.get(day) ?? 0) > 1),
  };
};

export const policyToForm = (policy: CardPaymentsRetryPolicy): PolicyFormValues => ({
  max_day: policy.max_day,
  combine: policy.combine,
  order: [...policy.order, ...RETRY_RULES.filter((rule) => !policy.order.includes(rule))],
  permitted_hours: [...policy.permitted_hours],
  schedule: Object.entries(policy.schedule).map(([days, hours]) => ({days, hours: [...hours]})),
  weekday_hours: Object.fromEntries(
    WEEKDAYS.map((weekday) => [weekday, [...(policy.weekday_hours[weekday] ?? [])]])
  ) as PolicyFormValues["weekday_hours"],
  skip_weekdays: [...policy.skip_weekdays],
  peak_day: [...policy.peak_day],
  peak_day_hours: [...policy.peak_day_hours],
  peak_min_day: policy.peak_min_day,
});

const sortedHours = (hours: number[]): number[] => [...new Set(hours)].sort((a, b) => a - b);

export const formToPolicy = (policy: PolicyFormValues): CardPaymentsRetryPolicy => ({
  max_day: policy.max_day,
  combine: policy.combine,
  order: policy.combine === "first_match" ? policy.order : [],
  permitted_hours: sortedHours(policy.permitted_hours),
  schedule: Object.fromEntries(
    policy.schedule.flatMap((row) => {
      const range = parseDayRange(row.days);
      return range ? [[formatDayRange(range), sortedHours(row.hours)]] : [];
    })
  ),
  weekday_hours: Object.fromEntries(
    WEEKDAYS.filter((weekday) => policy.weekday_hours[weekday].length > 0).map((weekday) => [
      weekday,
      sortedHours(policy.weekday_hours[weekday]),
    ])
  ),
  skip_weekdays: WEEKDAYS.filter((weekday) => policy.skip_weekdays.includes(weekday)),
  peak_day: policy.peak_day,
  peak_day_hours: sortedHours(policy.peak_day_hours),
  peak_min_day: policy.peak_min_day,
});

export const getDefaultControlRetryPolicy = (defaultFlow: CardPaymentsRetryFlow | undefined): CardPaymentsRetryPolicy =>
  getDefaultControlPolicy(defaultFlow) ?? EMPTY_POLICY;

export const buildVariantsFromFlag = (
  flag: PaymentFlowFeatureFlag,
  defaultFlow: CardPaymentsRetryFlow | undefined
): VariantFormValues[] =>
  Object.entries(flag.variant_weights).map(([name, weight]) => ({
    name,
    weight,
    label: defaultFlow?.config.variants[name]?.label ?? startCase(name),
    policy: policyToForm(getProductionPolicy(defaultFlow, name) ?? EMPTY_POLICY),
  }));

export const buildPaymentFlowPayload = (
  values: FormValues,
  parameters: ParamSchema[]
): PaymentFlowUpdatePayload<typeof CARD_PAYMENTS_RETRY_FLOW_TYPE> => ({
  name: values.name.trim(),
  type: CARD_PAYMENTS_RETRY_FLOW_TYPE,
  conditions: buildConditionsPayload(values, parameters),
  config: {
    feature_flag: values.feature_flag,
    variants: Object.fromEntries(
      values.variants.map((variant) => [
        variant.name,
        {weight: variant.weight, label: variant.label, policy: formToPolicy(variant.policy)},
      ])
    ),
  },
});

export const flowToForm = (flow: CardPaymentsRetryFlow): FormValues => ({
  ...flowToBaseForm(flow),
  variants: Object.entries(flow.config.variants).map(([name, variant]) => ({
    name,
    weight: variant.weight,
    label: variant.label,
    policy: policyToForm(variant.policy),
  })),
});
